import React, { useState } from 'react'

import { Edit as EditIcon, Home as HomeIcon, Print as PrintIcon } from '@material-ui/icons'

import { useAsyncPrint } from 'src/components/ToPrint/useAsyncPrint'
import { ASNLabel } from 'src/components/PrintLabel'
import { TransloadPlacard } from 'src/components/PrintLayout'
import { ASNDialog } from 'src/modules/supply_chain/components/ASNDialog'
import {
  AddressField,
  ASNAppointmentField,
  DateField,
  DateTimeField,
  StatusField,
  RelatedLinkedField,
  ValueField,
} from 'src/components/Table'
import { DetailComponent } from './base'

interface ObjInfo {
  data: any
  isLoading: boolean
  refetch: () => void
}

interface DetailTableProps {
  obj: ObjInfo
  refresh?: () => void
  customFields?: Record<string, string>
}

export const ASNDetailTable = (props: DetailTableProps) => {
  const { obj, refresh, customFields } = props
  const [updateOpen, setUpdateOpen] = useState(false)
  const { print: asyncPrint, renderPrintComponent: renderAsyncPrintComponent } = useAsyncPrint()

  const printActions = [
    {
      title: 'Print ASN',
      onClick: () => asyncPrint((props) => <ASNLabel {...props} asn={obj.data} />),
    },
  ]
  if (obj.data?.transloadNumber) {
    printActions.push(
      ...[
        {
          title: 'Print Transload Placard',
          onClick: () => asyncPrint((props) => <TransloadPlacard {...props} asn={obj.data} />),
        },
        {
          title: 'Print All',
          onClick: () =>
            asyncPrint((props) => (
              <>
                <ASNLabel asn={obj.data} />
                <TransloadPlacard {...props} asn={obj.data} />
              </>
            )),
        },
      ]
    )
  }

  return (
    <>
      {renderAsyncPrintComponent()}
      {updateOpen && (
        <ASNDialog
          open={updateOpen}
          asn={obj.data}
          update
          handleClose={() => {
            setUpdateOpen(false)
          }}
          onSave={refresh}
        />
      )}
      <DetailComponent
        obj={obj}
        Icon={HomeIcon}
        menuItems={[
          {
            Icon: EditIcon,
            actions: [{ title: 'Edit ASN', onClick: () => setUpdateOpen(true) }],
          },
          {
            Icon: PrintIcon,
            actions: printActions,
          },
        ]}
        fields={[
          { id: 'guid', label: 'SquID', renderer: ValueField },
          { id: 'trackingNumber', label: 'Tracking Number', renderer: ValueField },
          { id: 'bol', label: 'Bill of Lading', renderer: ValueField },
          { id: 'client', label: 'Client', renderer: RelatedLinkedField(ValueField, 'name') },
          {
            id: 'facility',
            label: 'Facility',
            renderer: RelatedLinkedField(ValueField, 'name'),
          },
          { id: 'transloadNumber', label: 'Transload', renderer: ValueField },
          { id: 'carrier', label: 'Carrier', renderer: RelatedLinkedField(ValueField, 'name') },
          { id: 'consignee', label: 'Consignee', renderer: ValueField },
          { id: 'address', label: 'Address', renderer: AddressField },
          { id: 'expectedPalletCount', label: 'Expected Pallet Count', renderer: ValueField },
          { id: 'status', label: 'Status', renderer: StatusField },
          { id: 'details', label: 'Details', renderer: ValueField },
          { id: 'internalNotes', label: 'Internal Notes', renderer: ValueField },
          { id: 'estimatedArrivalDate', label: 'Estimated Arrival Date', renderer: DateField },
          { id: 'createdOn', label: 'Created On', renderer: DateTimeField },
          { id: 'updatedOn', label: 'Updated On', renderer: DateTimeField },
          { id: 'receivedOn', label: 'Received On', renderer: DateTimeField },
          { id: 'canceledOn', label: 'Canceled On', renderer: DateTimeField },
          {
            id: 'appointment',
            label: 'Appointment',
            renderer: ASNAppointmentField({ refresh: obj.refetch }),
          },
        ]}
        customFields={customFields}
      />
    </>
  )
}
